import type { MultiStepFormData } from "@/stores";
import type { YesNoOption } from "@/utils";
import { formatDateForApi, PRODUCT_NAMES, YES_NO_OPTIONS } from "@/utils";

interface SubmitAllFormsRequest {
  externalLeadSourceId: number;
  leadSourceName: string;
  patientEmail: string;
  patientFirstName: string;
  patientLastName: string;
  patientDateOfBirth: string;
  patientPhoneNumber: string;
  patientMobileNumber: string;
  billingAddress: string;
  billingAddress2?: string | null;
  billingCity: string;
  billingState: string;
  billingZipCode: string;
  insuranceShippingSameAsBilling?: boolean | null;
  shippingAddress?: string;
  shippingAddress2?: string | null;
  shippingCity?: string;
  shippingState?: string;
  shippingZipCode?: string;
  mbiNumber: string;
  doctorNpi?: string | null;
  doctorFirstName: string;
  doctorLastName: string;
  doctorPhoneNumber: string;
  doctorFaxNumber: string;
  doctorStreetAddress: string;
  doctorStreetAddress2?: string;
  doctorCity: string;
  doctorState: string;
  doctorZipCode: string;
  doctorType: string;
  doctorAddressMatchGoogle?: boolean | null;
  doctorIsPecosCertified?: boolean | null;
  primaryInsuranceId: string;
  primaryInsurance: string;
  secondaryInsuranceId?: string;
  secondaryInsurance?: string;
  productName: string;
  productType?: string;
  notes: string;
  ppo?: boolean | null;
  hmo?: boolean | null;
  sendingAddress: string;
  planEffectiveDate: string;
  startUsingCgmDate?: string;
  sensorsDeliveryMethod: string;
  cancelledWithLastSupplier?: boolean;
  lastSensorsSupplierName?: string;
  lastSensorsReceivedAt?: string;
  lastSensorsReceivedQty?: string;
  usesInsulin?: boolean | null;
  usesInsulinPump?: boolean;
  insulinNames?: string[];
  insulinPerDay?: number;
  vendorId?: string;
  supplementalPolicyNumber?: string;
  supplementalPolicyName?: string;
  hasPermissionForTextCommunication: boolean;
  coInsurancePercentage?: string;
  stillHaveReader?: boolean;
}
function yesNoToBoolean(value?: YesNoOption): boolean {
  if (value === YES_NO_OPTIONS.YES) return true;
  return false;
}
export function buildSubmitAllFormsRequest(
  multiStepData: MultiStepFormData,
): SubmitAllFormsRequest | null {
  const {
    generalInformationFormData,
    medicalInformationFormData,
    addressInformationFormData,
    productInformationFormData,
    doctorInformationFormData,
    reviewInformationFormData,
    insuranceInformationFormData,
  } = multiStepData;

  let intInsulinDose = undefined;
  if (medicalInformationFormData?.dailyInsulinDose)
    intInsulinDose = parseInt(medicalInformationFormData.dailyInsulinDose);

  const requestPayload = {
    //General information form
    externalLeadSourceId: Number(generalInformationFormData?.listId),
    leadSourceName: generalInformationFormData?.leadSourceName ?? "",
    patientEmail: generalInformationFormData?.email ?? "",
    patientFirstName: generalInformationFormData?.firstName ?? "",
    patientLastName: generalInformationFormData?.lastName ?? "",
    patientDateOfBirth:
      formatDateForApi(generalInformationFormData?.dateOfBirth) ?? "",
    patientPhoneNumber: generalInformationFormData?.phoneNumber ?? "",

    //Address form
    patientMobileNumber: addressInformationFormData?.cellPhoneNumber ?? "",
    billingAddress:
      addressInformationFormData?.billingAddress.addressLineOne ?? "",
    billingAddress2: addressInformationFormData?.billingAddress.addressLineTwo,
    billingCity: addressInformationFormData?.billingAddress.city ?? "",
    billingState: addressInformationFormData?.billingAddress.state ?? "",
    billingZipCode: addressInformationFormData?.billingAddress.zipCode ?? "",
    insuranceShippingSameAsBilling: yesNoToBoolean(
      addressInformationFormData?.hasSameShippingAddress,
    ),
    shippingAddress:
      addressInformationFormData?.shippingAddress?.addressLineOne,
    shippingAddress2:
      addressInformationFormData?.shippingAddress?.addressLineTwo,
    shippingCity: addressInformationFormData?.shippingAddress?.city,
    shippingState: addressInformationFormData?.shippingAddress?.state,
    shippingZipCode: addressInformationFormData?.shippingAddress?.zipCode,

    //Doctor form
    mbiNumber: insuranceInformationFormData?.primaryPayerMbi ?? "",
    doctorNpi: doctorInformationFormData?.doctor.npi,
    doctorFirstName: doctorInformationFormData?.doctor.firstName ?? "",
    doctorLastName: doctorInformationFormData?.doctor.lastName ?? "",
    doctorPhoneNumber: doctorInformationFormData?.doctor.phoneNumber ?? "",
    doctorFaxNumber: doctorInformationFormData?.doctor.faxNumber ?? "",
    doctorStreetAddress: doctorInformationFormData?.doctor.addressLineOne ?? "",
    doctorStreetAddress2: doctorInformationFormData?.doctor.addressLineTwo,
    doctorCity: doctorInformationFormData?.doctor.city ?? "",
    doctorState: doctorInformationFormData?.doctor.state ?? "",
    doctorZipCode: doctorInformationFormData?.doctor.zipCode ?? "",
    doctorType: doctorInformationFormData?.doctor.specialty ?? "",
    doctorAddressMatchGoogle: doctorInformationFormData?.hasGoogleMatch,
    doctorIsPecosCertified: true,

    //Insurance form
    primaryInsuranceId: insuranceInformationFormData?.primaryPayerMbi ?? "",
    primaryInsurance: insuranceInformationFormData?.primaryPayer ?? "",
    secondaryInsuranceId: insuranceInformationFormData?.secondaryPayerId,
    secondaryInsurance: insuranceInformationFormData?.secondaryPayer,
    ppo: yesNoToBoolean(insuranceInformationFormData?.ppo),
    hmo: yesNoToBoolean(insuranceInformationFormData?.hmo),
    supplementalPolicyNumber:
      insuranceInformationFormData?.supplementaryPolicyNumber,
    supplementalPolicyName:
      insuranceInformationFormData?.supplementaryPolicyName,
    coInsurancePercentage: insuranceInformationFormData?.coInsurancePercentage,
    planEffectiveDate: insuranceInformationFormData?.planEffectiveDate ?? "",
    MSPType: insuranceInformationFormData?.msp?.MSPType,
    MSPPolicyNumber: insuranceInformationFormData?.msp?.policyNumber,
    MSPInsurerName: insuranceInformationFormData?.msp?.insurerName,
    MSPAddress1: insuranceInformationFormData?.msp?.address1,
    MSPCity: insuranceInformationFormData?.msp?.city,
    MSPState: insuranceInformationFormData?.msp?.state,
    MSPZip: insuranceInformationFormData?.msp?.zip,
    MSPStartDate: formatDateForApi(
      insuranceInformationFormData?.msp?.startDate,
    ),

    //Product form
    productName: productInformationFormData?.product ?? "",
    sensorsDeliveryMethod:
      productInformationFormData?.sensorsDeliveryMethod ?? "",
    cancelledWithLastSupplier:
      "cancelledWithLastSupplier" in productInformationFormData!
        ? yesNoToBoolean(productInformationFormData?.cancelledWithLastSupplier)
        : undefined,
    lastSensorsSupplierName:
      "lastSensorsSupplierName" in productInformationFormData!
        ? productInformationFormData.lastSensorsSupplierName
        : undefined,
    lastSensorsReceivedAt:
      "lastSensorsReceivedYear" in productInformationFormData!
        ? `${productInformationFormData.lastSensorsReceivedYear}-${String(
            productInformationFormData.lastSensorsReceivedMonth,
          ).padStart(2, "0")}`
        : "",
    lastSensorsReceivedQty:
      "lastSensorsReceivedQty" in productInformationFormData!
        ? productInformationFormData?.lastSensorsReceivedQty
        : "",
    productType:
      productInformationFormData?.product === PRODUCT_NAMES.SENSORS_ONLY &&
      "cgmReader" in productInformationFormData
        ? productInformationFormData.cgmReader
        : "",
    startUsingCgmDate:
      productInformationFormData?.product === PRODUCT_NAMES.SENSORS_ONLY &&
      "year" in productInformationFormData
        ? `${productInformationFormData.year}-${productInformationFormData.month}`
        : "",
    stillHaveReader:
      productInformationFormData?.product === PRODUCT_NAMES.SENSORS_ONLY &&
      "stillHaveReader" in productInformationFormData
        ? yesNoToBoolean(productInformationFormData.stillHaveReader)
        : undefined,

    //Medical form
    usesInsulin: yesNoToBoolean(medicalInformationFormData?.isTakingInsulin),
    usesInsulinPump:
      medicalInformationFormData?.dailyInsulinDose === "Using an insulin pump",
    insulinNames: medicalInformationFormData?.insulinNames,
    insulinPerDay: intInsulinDose,

    //Review form
    hasPermissionForTextCommunication: yesNoToBoolean(
      reviewInformationFormData?.hasTextCommunication,
    ),
    notes: reviewInformationFormData?.notes ?? "",
    sendingAddress: "test",
    vendorId: generalInformationFormData?.vendorId ?? "",
  };

  if (!requestPayload.secondaryInsuranceId) {
    delete requestPayload.secondaryInsurance;
    delete requestPayload.secondaryInsuranceId;
  }
  return requestPayload;
}
